import React, { useState, useEffect, useLayoutEffect, useRef, useContext } from 'react';
import ReactDOM from 'react-dom';
import { readOptions, addOption, removeOption, activeAfterRemoval } from '../../utils/variableOptions';
import { placePopup } from '../../utils/popupPlacement';
import { SUGGEST_DYNAMIC } from '../../utils/variableSuggestions';
// Circular by nature: the highlighter opens this editor, and this editor's value
// field is a highlighter. Safe because neither module touches the other at
// module scope — both references are inside a component body, evaluated at
// render. Keep it that way.
import SyntaxHighlightInput from './SyntaxHighlightInput';

/**
 * A name the rest of the app can round-trip: the highlighter captures `{{…}}`
 * with `[^}]+`, so braces are out; `$` is out because `{{$name}}` is claimed
 * by the dynamic-variable resolver (click-to-edit would stop working on it);
 * spaces are out because the token is pasted into a URL. Same character set
 * as an ordinary identifier, plus `.` and `-` which existing keys already use.
 */
export const VARIABLE_NAME_RE = /^[A-Za-z_][A-Za-z0-9_.-]*$/;

export function isValidVariableName(name) {
  return typeof name === 'string' && VARIABLE_NAME_RE.test(name);
}

/**
 * How deep a stack of editors this one is.
 *
 * Clicking a `{{…}}` chip in a value opens THAT variable's editor on top, so
 * following a chain of references leaves several open at once. Each has its own
 * click-outside rule, and they have to be able to tell each other apart: a
 * click on an editor ABOVE me is my own subtree still being used, while a click
 * on the one BELOW me means the user came back down and I should commit.
 *
 * DOM nesting cannot answer that — every editor portals to `<body>` and ends up
 * a sibling. React context can, because it flows through a portal.
 */
const EditorDepthContext = React.createContext(0);

/**
 * The editor stack's z-index, and the ceiling above it.
 *
 * Each nested editor sits one above its parent. The variable tooltip has to
 * clear the whole stack: it used to share the base, so hovering a chip while a
 * nested editor was open drew the hint BEHIND it (owner, 23.09.2026). The gap
 * is 900 because a tooltip is transient and a stack that deep is not a thing.
 */
export const VAR_EDITOR_BASE_Z = 100001;
export const VAR_TOOLTIP_Z = VAR_EDITOR_BASE_Z + 900;

const SCOPE_META = {
  collection: { label: 'Collection',   description: 'Scoped to this collection' },
  environment: { label: 'Environment', description: 'Active environment' },
  workspace:   { label: 'Workspace',   description: 'Shared across this workspace' },
  global:      { label: 'Global',      description: 'Available everywhere' },
};

function ScopeIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M2 5.5C2 4.67 2.67 4 3.5 4H7L8.5 5.5H14.5C15.33 5.5 16 6.17 16 7V13.5C16 14.33 15.33 15 14.5 15H3.5C2.67 15 2 14.33 2 13.5V5.5Z"
        fill="currentColor" fillOpacity="0.9"/>
    </svg>
  );
}

function ChevronIcon({ open }) {
  return (
    <svg width="12" height="12" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg"
      style={{ transform: open ? 'rotate(180deg)' : 'none', transition: 'transform 0.15s ease' }}>
      <path d="M4 6L8 10L12 6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  );
}

const VariableEditorPopup = ({
  editingVariable,
  variableContext,
  selectedEnvironment,
  // The scopes the VALUE field highlights against — without them a perfectly
  // good `{{baseUrl}}` typed in here would render as missing.
  workspaceVariables,
  globalVariables,
  collectionVariables,
  folderVariables,
  // How a variable is written. Passed down so that a NESTED editor — opened by
  // clicking a chip in the value below — saves through the same path this one
  // does, rather than falling back to the "write it into the selected
  // environment" event, which writes nowhere when none is selected.
  onVariableUpdate,
  /**
   * Whether to offer the SCOPE picker.
   *
   * Off when the opener is a table that IS one scope — the Environments editor,
   * collection variables. Moving the row somewhere else from there would be a
   * write that table has no handler for, and offering a control that silently
   * does nothing is worse than not offering it.
   */
  showScope = true,
  onSave,
  onClose,
}) => {
  const [value, setValue] = useState(editingVariable.value || '');
  // Picking a scope saves and closes immediately, so this only ever holds the opening scope.
  const [scope] = useState(editingVariable.type || 'global');
  const [scopeOpen, setScopeOpen] = useState(false);
  const [dropdownRect, setDropdownRect] = useState(null);
  // The `{{name}}` chip is an input ONLY when the opener says so (`nameEditable: true` — the
  // "To Variable" flow, where no name exists yet). `exists === false` alone is NOT enough:
  // clicking a missing `{{foo}}` in the URL opens "Add variable" with exists:false, and its
  // name must stay fixed or a rename would leave the URL token pointing at the old name.
  const nameEditable = editingVariable.nameEditable === true;
  const [name, setName] = useState(editingVariable.name || '');
  // Alternatives this variable remembers. `value` stays the ACTIVE one, so
  // resolution is untouched — see utils/variableOptions.js.
  const [options, setOptions] = useState(() => readOptions(editingVariable));
  // 0 for an editor opened from a field; 1+ for one opened from a chip inside
  // another editor's value.
  const depth = useContext(EditorDepthContext);
  const popupRef = useRef(null);
  const triggerRef = useRef(null);
  const inputRef = useRef(null);
  const nameInputRef = useRef(null);
  // Keep latest value/scope/name accessible in event handlers without stale closures.
  const valueRef = useRef(value);
  const scopeRef = useRef(scope);
  const nameRef = useRef(name);
  const optionsRef = useRef(options);
  valueRef.current = value;
  optionsRef.current = options;
  scopeRef.current = scope;
  nameRef.current = name;
  const nameValid = !nameEditable || isValidVariableName(name);

  // Editable name: the user has to type it first, so the chip gets the focus; otherwise VALUE.
  useEffect(() => {
    if (nameEditable) nameInputRef.current?.focus();
    else inputRef.current?.focus();
  }, [nameEditable]);

  // Every save goes through here. An editable name that is empty or malformed refuses the
  // save (the chip is focused instead) and NOTHING is written — otherwise `{{}}` or `{{my var}}`
  // would land in the URL and in a scope store. Non-editable openings always pass `nameRef`
  // (= editingVariable.name), so callers that read two args see exactly what they saw before.
  const commitSave = (s) => {
    if (nameEditable && !isValidVariableName(nameRef.current)) {
      nameInputRef.current?.focus();
      return;
    }
    // Only pass options when there ARE any: an ordinary variable's save stays
    // byte-identical to what it has always been, so nothing downstream — or in
    // the existing tests — has to learn a new signature it does not need.
    if (optionsRef.current.length) onSave(valueRef.current, s, nameRef.current, optionsRef.current);
    else onSave(valueRef.current, s, nameRef.current);
  };

  // Save + close when clicking outside; close dropdown when clicking outside trigger+dropdown.
  useEffect(() => {
    const handler = (e) => {
      const inPopup = popupRef.current?.contains(e.target);
      // Things that belong to this popup while living outside its DOM, all
      // portaled to `<body>` to escape its `overflow: hidden`: the scope menu
      // and the VALUE field's `{{…}}` suggestions list. Either click is the
      // middle of an edit; treating it as walking away would save and close the
      // editor out from under it.
      const ownFloating = e.target.closest?.('.var-editor-scope-dropdown, .dyn-var-suggestions');
      // And the editors STACKED ON TOP of this one, opened by clicking a chip in
      // the value below. Those are this editor still being used. An editor
      // BELOW is not: clicking back down to it means the user is done up here,
      // and this one commits like any other click-away.
      const other = e.target.closest?.('.var-editor');
      const aboveMe = other && Number(other.dataset.editorDepth || 0) > depth;
      const inDropdown = Boolean(ownFloating) || Boolean(aboveMe);
      if (!inPopup && !inDropdown) {
        // Editable name still empty/invalid: the user walked away before naming it — dismiss,
        // write nothing, leave the URL untouched.
        if (nameEditable && !isValidVariableName(nameRef.current)) {
          onClose();
          return;
        }
        // Only persist on click-away if the user actually entered or changed something. Opening a
        // variable and clicking away without typing should just dismiss the editor, not auto-create
        // an empty variable (previously it saved a blank value into the collection scope).
        const initialValue = editingVariable.value || '';
        const initialScope = editingVariable.type || 'global';
        const initialName = editingVariable.name || '';
        const initialOptions = readOptions(editingVariable);
        const changed =
          valueRef.current !== initialValue ||
          scopeRef.current !== initialScope ||
          nameRef.current !== initialName ||
          JSON.stringify(optionsRef.current) !== JSON.stringify(initialOptions);

        if (changed) {
          if (optionsRef.current.length) {
            onSave(valueRef.current, scopeRef.current, nameRef.current, optionsRef.current);
          } else {
            onSave(valueRef.current, scopeRef.current, nameRef.current);
          }
        } else {
          onClose();
        }
      } else if (scopeOpen && !inDropdown && !triggerRef.current?.contains(e.target)) {
        setScopeOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [scopeOpen, onSave, onClose, editingVariable, nameEditable, depth]);

  // Recalculate dropdown position when scope dropdown opens or window scrolls/resizes.
  useEffect(() => {
    if (!scopeOpen) return;
    const update = () => {
      if (triggerRef.current) setDropdownRect(triggerRef.current.getBoundingClientRect());
    };
    update();
    window.addEventListener('scroll', update, true);
    window.addEventListener('resize', update);
    return () => {
      window.removeEventListener('scroll', update, true);
      window.removeEventListener('resize', update);
    };
  }, [scopeOpen]);

  const availableScopes = [
    variableContext?.collectionId ? 'collection' : null,
    selectedEnvironment ? 'environment' : null,
    variableContext?.workspaceId ? 'workspace' : null,
    'global',
  ].filter(Boolean);

  const handleScopeToggle = () => {
    if (!scopeOpen && triggerRef.current) {
      setDropdownRect(triggerRef.current.getBoundingClientRect());
    }
    setScopeOpen((o) => !o);
  };

  const handleScopeSelect = (s) => {
    setScopeOpen(false);
    commitSave(s);
  };

  const { x, y } = editingVariable;

  /**
   * The popup opens at the coordinates of the chip that was clicked, which is
   * wrong near a window edge — and the request-variables panel lives in the
   * bottom-right corner by design, so "near an edge" is its normal case.
   *
   * Measured rather than assumed: the height depends on whether the variable
   * has options and how many, so a constant would be wrong for exactly the tall
   * popups that overflow. `useLayoutEffect` + a ResizeObserver means the move
   * happens before paint and again when adding an option grows it.
   */
  const [place, setPlace] = useState(() => ({ left: x, top: y + 10 }));

  useLayoutEffect(() => {
    const node = popupRef.current;
    if (!node) return undefined;
    const reposition = () => {
      const { width, height } = node.getBoundingClientRect();
      // jsdom, and any element not laid out yet, measures zero — placing
      // against that would compute a corner rather than a position.
      if (!width || !height) return;
      setPlace(
        placePopup({
          x,
          y,
          width,
          height,
          viewportWidth: window.innerWidth,
          viewportHeight: window.innerHeight,
        })
      );
    };
    reposition();
    const observer = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(reposition) : null;
    observer?.observe(node);
    window.addEventListener('resize', reposition);
    return () => {
      observer?.disconnect();
      window.removeEventListener('resize', reposition);
    };
  }, [x, y]);

  return ReactDOM.createPortal(
    <>
      <div
        ref={popupRef}
        className="var-editor"
        /* Read by every OTHER editor's click-outside rule to tell "my own
           nested editor" from "the one I was opened from". */
        data-editor-depth={depth}
        style={{ left: place.left, top: place.top, zIndex: VAR_EDITOR_BASE_Z + depth }}
      >
        {/* Header */}
        <div className="var-editor-header">
          <span className="var-editor-title">
            {editingVariable.exists ? 'Edit variable' : 'Add variable'}
          </span>
          {nameEditable ? (
            <code className={`var-editor-chip${nameValid ? '' : ' var-editor-chip--invalid'}`}>
              {'{{'}
              <input
                ref={nameInputRef}
                type="text"
                className="var-editor-chip-input"
                value={name}
                placeholder="variableName"
                spellCheck={false}
                autoComplete="off"
                style={{ width: `${Math.max(name.length, 12)}ch` }}
                onChange={(e) => setName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') commitSave(scopeRef.current);
                  if (e.key === 'Escape') onClose();
                }}
              />
              {'}}'}
            </code>
          ) : (
            <code className="var-editor-chip">{`{{${editingVariable.name}}}`}</code>
          )}
        </div>

        {/* Body */}
        <div className="var-editor-body">
          <div className="var-editor-field">
            <label className="var-editor-label">VALUE</label>
            {/* The highlighter, not a plain input: a variable's value very often
                names another variable, and `{{baseUrl}}/v1` typed here used to
                be the one place in the app where that read as flat text — so
                you could not see whether the name you had typed resolves
                (owner, 23.09.2026).

                Its chips are LIVE: clicking one opens that variable's own editor
                on top of this one (owner, same day). A chain of references
                becomes a chain of editors — follow `{{baseUrl}}` into `{{host}}`
                and fix each where you found it, instead of closing this one and
                hunting for the next in a table.

                The suggestions stay `$`-only. A pick-list of the variables you
                are in the middle of defining one of is the self-reference knot
                that rule already exists to keep out of the variables table. */}
            <EditorDepthContext.Provider value={depth + 1}>
            <SyntaxHighlightInput
              inputRef={inputRef}
              className="var-editor-input"
              value={value}
              onChange={(e) => setValue(e.target.value)}
              placeholder="Enter value…"
              onVariableUpdate={onVariableUpdate}
              suggest={SUGGEST_DYNAMIC}
              selectedEnvironment={selectedEnvironment}
              workspaceVariables={workspaceVariables}
              globalVariables={globalVariables}
              collectionVariables={collectionVariables}
              folderVariables={folderVariables}
              variableContext={variableContext}
              aria-label="Value"
              onKeyDown={(e) => {
                // Enter ADDS an alternative when the variable has any, instead of
                // saving — a list you cannot extend from the field you are typing
                // in would send you hunting for a plus button.
                if (e.key === 'Enter') {
                  if (options.length && value.trim() && !options.includes(value.trim())) {
                    e.preventDefault();
                    setOptions((o) => addOption({ options: o }, value));
                    return;
                  }
                  commitSave(scopeRef.current);
                }
                if (e.key === 'Escape') onClose();
              }}
            />
            </EditorDepthContext.Provider>
            {options.length === 0 && value.trim() !== '' && (
              <button
                type="button"
                className="var-editor-add-option"
                onClick={() => setOptions((o) => addOption({ options: o }, value))}
              >
                + Add as option
              </button>
            )}
          </div>

          {options.length > 0 && (
            <div className="var-editor-field">
              <label className="var-editor-label">OPTIONS</label>
              <div className="var-editor-options">
                {options.map((opt) => (
                  <div
                    key={opt}
                    className={`var-editor-option${opt === value ? ' var-editor-option--active' : ''}`}
                  >
                    {/* Picking sets the ACTIVE value; the alternatives stay. */}
                    <button
                      type="button"
                      className="var-editor-option-pick"
                      onClick={() => setValue(opt)}
                      title={opt}
                    >
                      {opt}
                    </button>
                    <button
                      type="button"
                      className="var-editor-option-remove"
                      aria-label={`Remove option ${opt}`}
                      onClick={() => {
                        // Removing the ACTIVE option would leave the variable
                        // resolving to something no longer on its own list.
                        setValue((v) => activeAfterRemoval({ options }, opt, v));
                        setOptions((o) => removeOption({ options: o }, opt));
                      }}
                    >
                      ×
                    </button>
                  </div>
                ))}
              </div>
              <div className="var-editor-hint">Type a value and press Enter to add another</div>
            </div>
          )}

          {showScope && (
          <div className="var-editor-field">
            <label className="var-editor-label">SCOPE</label>
            <div className="var-editor-scope">
              <button
                ref={triggerRef}
                type="button"
                className="var-editor-scope-trigger"
                onClick={handleScopeToggle}
              >
                <span className="var-editor-scope-icon"><ScopeIcon /></span>
                <span className="var-editor-scope-text">
                  <span className="var-editor-scope-name">{SCOPE_META[scope]?.label}</span>
                  <span className="var-editor-scope-desc">{SCOPE_META[scope]?.description}</span>
                </span>
                <span className="var-editor-scope-chevron"><ChevronIcon open={scopeOpen} /></span>
              </button>
            </div>
          </div>
          )}
        </div>
      </div>

      {/* Scope dropdown — portaled to body so it escapes the popup's overflow */}
      {scopeOpen && dropdownRect && ReactDOM.createPortal(
        <div
          className="var-editor-scope-dropdown"
          style={{
            position: 'fixed',
            top: dropdownRect.bottom + 4,
            left: dropdownRect.left,
            width: dropdownRect.width,
            zIndex: VAR_EDITOR_BASE_Z + depth + 1,
          }}
        >
          {availableScopes.map((s) => (
            <button
              key={s}
              type="button"
              className={`var-editor-scope-option${s === scope ? ' var-editor-scope-option--active' : ''}`}
              onClick={() => handleScopeSelect(s)}
            >
              <span className="var-editor-scope-icon"><ScopeIcon /></span>
              <span className="var-editor-scope-text">
                <span className="var-editor-scope-name">{SCOPE_META[s]?.label}</span>
                <span className="var-editor-scope-desc">{SCOPE_META[s]?.description}</span>
              </span>
            </button>
          ))}
        </div>,
        document.body
      )}
    </>,
    document.body
  );
};

export default VariableEditorPopup;
